<template>
  <o-dialog
    ref="dialog"
    :title="$t('components.purchaseOrder.actions.print.title')"
    :info="info"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('general.confirm'), type: 'confirm', variant: 'success', callback: confirm },
    ]"
    :portal="portal"
  >
    <div v-if="purchaseOrder" class="-mx-4 border-t- border-b p-4 bg-gray-50 dark:bg-gray-900">
      <!--<o-field-group label="Page size" class="mb-4 block">
        <select-field
          v-model="options.pageSize"
          :options="[
            'A4'
          ]"
        />
      </o-field-group>-->

      <o-field-group :label="$t('general.notes')" class="block">
        <select-field
          v-model="options.pageOrientation"
          :options="[
            { value: 'portrait', label: $t('components.purchaseOrder.actions.print.portrait') },
            { value: 'landscape', label: $t('components.purchaseOrder.actions.print.landscape') }
          ]"
        />
      </o-field-group>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import { SelectField } from '@/components/fields'
import { OFieldGroup } from '@/components/elements'

import type { PurchaseOrder } from '~/_api/models/purchaseOrder'

interface Props {
  portal?: string
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
})

const { $i18n, $currency, $date } = useNuxtApp()

const dialog = ref<any>()
const purchaseOrder = ref<PurchaseOrder | null>(null)
const options = ref({
  pageOrientation: 'portrait',
  pageSize: 'A4',
})

const info = computed(() => {
  return $i18n.t('components.purchaseOrder.actions.print.info', { name: (purchaseOrder.value as any)?.supplier.name, number: (purchaseOrder.value as any)?.purchaseOrderNumber })
})

const open = (po: PurchaseOrder) => {
  purchaseOrder.value = po

  dialog.value.open()
}

const confirm = async () => {
  const po = purchaseOrder.value as any
  const { addressFrom, addressTo, addressShip, data } = po
  const { pageOrientation } = options.value

  const products = data.items.map((item: any) => {
    return [
      item.orderQuantity || '-',
      item.brand || '-',
      item.title || '-',
      item.code || '-',
      $currency.format(item.price),
      $currency.format(item.price * item.orderQuantity),
    ]
  })

  const taxes = data.taxTotals.map((tax: any) => {
    return [
      tax.tax.name,
      $currency.format(tax.total),
    ]
  })

  const doc = {
    content: [
      {
        text: $i18n.t('purchaseOrder.title2', { label: po.supplier.name }),
        style: 'header',
        margin: [0, 0, 0, 10],
      },
      {
        text: [
          $i18n.t('general.colon', { label: $i18n.t('general.date') }),
          { text: po.date ? $date.format(po.date * 1000, $date.presets.full) : '-', bold: true },
        ],
        style: 'small',
      },
      {
        text: [
          $i18n.t('components.purchaseOrder.actions.print.number'),
          { text: po.purchaseOrderNumber, bold: true },
        ],
        style: 'small',
        margin: [0, 0, 0, 10],
      },
      {
        columns: [
          [
            { text: $i18n.t('general.from'), style: 'small', bold: true },
            { text: addressFrom, style: 'small' },
          ],
          [
            { text: $i18n.t('general.to'), style: 'small', bold: true },
            { text: addressTo, style: 'small' },
          ],
          [
            { text: $i18n.t('components.purchaseOrder.actions.print.shipTo'), style: 'small', bold: true },
            { text: addressShip, style: 'small' },
          ],
        ],
        margin: [0, 0, 0, 20],
      },
      {
        layout: {
          hLineWidth () { return 0.5 },
          vLineWidth () { return 0.5 },
          hLineColor () { return 'gray' },
          vLineColor () { return 'gray' },
        },
        table: {
          headerRows: 1,

          body: [
            [$i18n.t('general.quantity'), $i18n.t('general.brand'), $i18n.t('general.product'), $i18n.t('general.code'), $i18n.t('components.purchaseOrder.actions.print.unitPrice'), $i18n.t('components.purchaseOrder.actions.print.total')],
            ...products,
          ],

          widths: ['auto', 'auto', 'auto', 'auto', 'auto', 'auto'],

          style: 'small',
        },
        margin: [0, 0, 0, 20],
      },
      {
        columns: [
          { width: '*', text: '' },
          {
            width: 'auto',
            layout: 'noBorders',
            table: {
              body: [
                ['Subtotal', $currency.format(data.totals.subtotal)],
                ...taxes,
                ['Total (inc Tax)', $currency.format(data.totals.total)],
              ],
            },
          },
        ],
      },
    ],
    styles: {
      header: {
        fontSize: 18,
        bold: true,
      },
      subheader: {
        fontSize: 15,
        bold: true,
      },
      quote: {
        italics: true,
      },
      small: {
        fontSize: 9,
      },
    },
    defaultStyle: {
      fontSize: 9,
    },
    pageMargins: [15, 15, 15, 15],
    pageOrientation,
  }

  const pdfMake = await usePdfMake()
  pdfMake.createPdf(doc).open()

  return true
}

defineExpose({ open })
</script>
